import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Chip,
    useTheme,
    Paper,
    Container,
    Stack,
    IconButton,
} from '@mui/material';
import {
    ShoppingCart,
    Payment,
    VerifiedUser,
    Diamond,
    Extension,
    Security,
    CheckCircleOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { Cart } from '../../types/cart';

const CartCheckout = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { updateSubscriptionStatus } = useAuth();
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Fetch cart
    const { data: cartData, isLoading: cartLoading } = useQuery({
        queryKey: ['my-cart'],
        queryFn: async () => {
            const response = await api.get('/tenant/cart');
            return response.data?.data as Cart;
        },
    });

    // Create order and process payment
    const handleCheckout = async () => {
        if (!cartData || !cartData.plan_id) {
            setError('Please select a plan first');
            return;
        }

        setIsProcessing(true);
        setError('');

        try {
            // Create Razorpay order
            const orderRes = await api.post('/tenant/payments/cart/create-order');
            const orderData = orderRes.data?.data;

            if (!orderData?.razorpay_order_id) {
                throw new Error('Failed to create order');
            }

            // Open Razorpay checkout
            const options = {
                key: orderData.razorpay_key,
                amount: orderData.amount,
                currency: orderData.currency,
                name: 'Leader360',
                description: 'Subscription Payment',
                order_id: orderData.razorpay_order_id,
                handler: async (response: any) => {
                    try {
                        // Verify payment
                        const verifyRes = await api.post('/tenant/payments/cart/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });

                        if (verifyRes.data?.status === 'success') {
                            // Update subscription status
                            updateSubscriptionStatus(true);
                            // Redirect to dashboard
                            window.location.href = '/admin';
                        } else {
                            setError('Payment verification failed');
                        }
                    } catch (err: any) {
                        setError(err.response?.data?.message || 'Payment verification failed');
                    }
                    setIsProcessing(false);
                },
                modal: {
                    ondismiss: () => {
                        setIsProcessing(false);
                    },
                },
                prefill: {
                    name: orderData.prefill?.name,
                    email: orderData.prefill?.email,
                    contact: orderData.prefill?.contact,
                },
                theme: {
                    color: theme.palette.primary.main,
                },
            };

            const razorpay = new (window as any).Razorpay(options);
            razorpay.open();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to initiate payment');
            setIsProcessing(false);
        }
    };

    if (cartLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    const cart = cartData;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    Checkout
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Review your subscription details and complete your secure payment.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">
                {/* Left Column: Cart Items */}
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Stack spacing={3}>
                        {/* Plan Card */}
                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Diamond color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Selected Plan
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {cart?.plan ? (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                                            {cart.plan.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Billing Cycle: Monthly
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight="bold">
                                        ₹{cart.plan.price?.toLocaleString()}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography color="text.secondary">No plan selected.</Typography>
                                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/admin/plans')}>
                                        Select a Plan
                                    </Button>
                                </Box>
                            )}
                        </Paper>

                        {/* Addons Card */}
                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Extension color="secondary" sx={{ mr: 1.5, fontSize: 28 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Add-ons
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {cart?.items && cart.items.length > 0 ? (
                                <List disablePadding>
                                    {cart.items.map((item) => (
                                        <ListItem key={item.id} sx={{ px: 0, py: 2, borderBottom: `1px dashed ${theme.palette.divider}` }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: theme.palette.secondary.light, color: theme.palette.secondary.main }}>
                                                    <Extension fontSize="small" />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1" fontWeight="600">
                                                        {item.addon?.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Chip
                                                        label={item.addon?.type}
                                                        size="small"
                                                        sx={{ mt: 0.5, bgcolor: theme.palette.grey[100] }}
                                                    />
                                                }
                                            />
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    ₹{item.unit_price} × {item.quantity}
                                                </Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    ₹{item.total_price?.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography color="text.secondary" sx={{ py: 2 }}>
                                    No add-ons selected.
                                </Typography>
                            )}
                        </Paper>
                    </Stack>
                </Box>

                {/* Right Column: Summary */}
                <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0 }}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            position: 'sticky',
                            top: 24,
                            bgcolor: theme.palette.background.paper,
                            backgroundImage: 'linear-gradient(to bottom right, rgba(255,255,255,1), rgba(250,250,255,0.5))'
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Order Summary
                        </Typography>
                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={2} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Subtotal (Plan)</Typography>
                                <Typography fontWeight="500">
                                    ₹{cart?.plan?.price?.toLocaleString() || 0}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Add-ons Total</Typography>
                                <Typography fontWeight="500">
                                    ₹{((cart?.total_amount || 0) - (cart?.plan?.price || 0)).toLocaleString()}
                                </Typography>
                            </Box>
                            {/* Tax could go here */}
                        </Stack>

                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4 }}>
                            <Typography variant="h6">Total Amount</Typography>
                            <Typography variant="h4" fontWeight="bold" color="primary.main">
                                ₹{(cart?.total_amount || 0).toLocaleString()}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                            onClick={handleCheckout}
                            disabled={!cart?.plan_id || isProcessing}
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                boxShadow: theme.shadows[4]
                            }}
                        >
                            {isProcessing ? 'Processing Payment...' : 'Secure Checkout'}
                        </Button>

                        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', gap: 1 }}>
                            <Security fontSize="small" />
                            <Typography variant="caption" align="center">
                                Payments are secured by Razorpay
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Stack>
        </Container>
    );
};

export default CartCheckout;
